import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Col, Modal, ModalBody, Row } from "reactstrap";
import { css } from '@emotion/react';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Box, IconButton, Divider, LinearProgress } from '@mui/material';
import Barcode from "react-barcode";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import html2canvas from 'html2canvas';

const printStyles = css`
  @media print {
    .printContainer {
      width: 100%;
      height: 100%;
      display: block;
      background-color: white;
      color: black;
    }
    .printButton {
      display: none;
    }
  }
`;

const OrderDetailsShippingViewDetailsModal = ({ modalShow, setModalShow, dataInfo, isTitle }) => {
    const [isOrderItemDetails, setOrderItemDetails] = useState([]);
    const [barcodePngs, setBarcodePngs] = useState([]);
    const [loading, setLoading] = useState(false);
    const svgRefs = useRef([]);

    useEffect(() => {
        if (modalShow && dataInfo?.orderId) {
            getOrderItemWiseDetails();
        }
    }, [modalShow, dataInfo]);

    const getOrderItemWiseDetails = async () => {
        setLoading(true);
        try {
            const response = await PndRestApi.GetOrderItemWiseDetails(dataInfo.orderId);
            if (response.status === 200 && response.data.status === 200) {
                setOrderItemDetails(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching item details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOrderItemDetails.length > 0) {
            fetchBarcodes();
        }
    }, [isOrderItemDetails]);

    const fetchBarcodes = async () => {
        try {
            const barcodes = await Promise.all(svgRefs.current.map(async (ref) => {
                if (!ref) return null;
                const canvas = await html2canvas(ref);
                return canvas.toDataURL('image/png');
            }));
            setBarcodePngs(barcodes.filter(Boolean));
        } catch (error) {
            console.error("Error generating barcodes:", error);
        }
    };

    const toggle = () => setModalShow(prevState => !prevState);

    const handlePrint = (item, index) => {
        const printWindow = window.open('', '_blank');
        const currentDate = new Date().toLocaleString();

        const html = `
            <html>
                <head>
                    <title>Reschedule Shipment Label - ${dataInfo?.orderNo}</title>
                    <style>
                        body { font-family: 'Inter', sans-serif; margin: 40px; }
                        .center { text-align: center; }
                        .card { border: 1px solid #ddd; padding: 15px; margin: 20px 0; border-radius: 8px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                        th, td { border: 1px solid #eee; padding: 10px; text-align: left; }
                    </style>
                </head>
                <body>
                    <h2 class="center">Rescheduled Delivery Label</h2>
                    <p class="center">Order No: <b>${dataInfo?.orderNo}</b></p>
                    <div class="card">
                        <b>DESTINATION</b><br>
                        ${dataInfo?.address || 'N/A'}<br>
                        Contact: ${dataInfo?.contactNo}
                    </div>
                    <table>
                        <thead>
                            <tr><th>Sl</th><th>Barcode</th><th>Weight</th><th>Dimensions</th></tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>1</td><td>${item.boxBarCode}</td>
                                <td>${item.weightKg || '--'} Kg</td><td>${item.boxDimension || '--'}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="center" style="margin-top: 30px;">
                        <img src="${barcodePngs[index]}" style="max-width: 450px; width: 100%;" />
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
    };

    const order = isOrderItemDetails[0]?.orderInfo || {};
    return (
        <Modal
            isOpen={modalShow}
            toggle={toggle}
            className="theme-light ltr-support credit-memo-modal-form-ani"
            centered
            style={{ maxWidth: '1000px' }}
        >
            <div className="modal__header border-bottom-0 pb-0">
                <button
                    className="lnr lnr-cross modal__close-btn"
                    aria-label="modal__close-btn"
                    type="button"
                    onClick={toggle}
                />
                <div className="d-flex align-items-center">
                    <CalendarTodayIcon className="text-warning me-2" style={{ fontSize: '1.5rem' }} />
                    <h5 className="modal__title text-uppercase fw-bold m-0" style={{ color: "#32CA71", fontSize: '1.1rem' }}>
                        {isTitle || "Reschedule Shipment Details"}
                        <Box
                            component="span"
                            sx={{
                                backgroundColor: order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.colorCode || '#32CA71',
                                color: 'white',
                                fontSize: '0.7rem',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '12px',
                                ml: 1,
                                verticalAlign: 'middle',
                                display: 'inline-block',
                                fontWeight: 600,
                                textTransform: 'none'
                            }}
                        >
                            {order?.customerDeliveryTagMappingInfo?.customerDeliveryTagInfo?.tagName}
                        </Box>
                    </h5>
                </div>
            </div>
            <ModalBody className="p-3">
                {loading && <LinearProgress sx={{ mt: -3, mb: 2 }} />}

                <Box className="printContainer" css={printStyles}>
                    {/* Identification */}
                    <Paper elevation={0} className="p-3 mb-3 border rounded bg-light shadow-sm">
                        <Row className="g-2 text-center">
                            <Col md={4}>
                                <Typography className="text-muted small fw-bold text-uppercase">Tracking No</Typography>
                                <Typography className="fw-bold text-primary">{dataInfo?.orderNo || '--'}</Typography>
                            </Col>
                            <Col md={4} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Invoice</Typography>
                                <Typography className="fw-bold">{dataInfo?.invoiceNo || 'N/A'}</Typography>
                            </Col>
                            <Col md={4} className="border-start">
                                <Typography className="text-muted small fw-bold text-uppercase">Sales (Ecom)</Typography>
                                <Typography className="fw-bold">{dataInfo?.salesOrderNo || '--'}</Typography>
                            </Col>
                        </Row>
                    </Paper>

                    {/* Content Row */}
                    <Row className="g-3 mb-3">
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'success.main' }}>
                                    <LocationOnIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Client / Recipient</Typography>
                                </Box>
                                <Typography variant="body2" className="text-muted small mb-2" style={{ lineHeight: 1.6 }}>
                                    {dataInfo?.address || "N/A"}
                                </Typography>
                                <div className="d-flex flex-wrap gap-1">
                                    {dataInfo?.contactNo && <Chip label={`Tel: ${dataInfo.contactNo}`} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                                    {dataInfo?.name && <Chip label={dataInfo.name} size="small" variant="outlined" sx={{ borderRadius: 1 }} />}
                                </div>
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded bg-slate-50">
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'warning.main' }}>
                                    <CalendarTodayIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Reschedule Timestamps</Typography>
                                </Box>
                                <div className="small text-muted">
                                    <div className="d-flex justify-content-between mb-1 pb-1 border-bottom">
                                        <span>Requested Slot:</span> <span className="fw-bold text-dark">{dataInfo?.requestedDateTimeSlot || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-1 pb-1 border-bottom">
                                        <span>Scheduled Slot:</span> <span className="fw-bold text-dark">{dataInfo?.scheduledDateTimeSlot || '--'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Last Picked Up:</span> <span className="fw-bold text-dark">{dataInfo?.pickedUpDateTime || '--'}</span>
                                    </div>
                                </div>
                            </Paper>
                        </Col>
                        <Col lg={4}>
                            <Paper elevation={0} className="p-3 h-100 border rounded">
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'info.main' }}>
                                    <PaymentIcon fontSize="small" className="me-1" />
                                    <Typography variant="subtitle2" className="fw-bold">Financial Status</Typography>
                                </Box>
                                <div className="p-2 bg-light rounded border small">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Method:</span> <span className="fw-bold">{dataInfo?.paymentMethodInfo?.description || '--'}</span>
                                    </div>
                                    <Divider className="my-1" />
                                    <div className="d-flex justify-content-between text-primary fw-bold">
                                        <span>COLLECTABLE:</span> <span>{dataInfo?.riderCollectionAmount || '0.00'}</span>
                                    </div>
                                </div>
                            </Paper>
                        </Col>
                    </Row>

                    {/* Table */}
                    <TableContainer component={Paper} elevation={0} className="border rounded mb-3 overflow-hidden">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                <TableRow>
                                    <TableCell className="fw-bold">#</TableCell>
                                    <TableCell className="fw-bold">Box Barcode</TableCell>
                                    <TableCell className="fw-bold">Qty</TableCell>
                                    <TableCell className="fw-bold">Weight/Dim</TableCell>
                                    <TableCell className="fw-bold text-center">In/Out</TableCell>
                                    <TableCell className="fw-bold">Barcode</TableCell>
                                    <TableCell className="fw-bold text-center">Label</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isOrderItemDetails.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell className="fw-bold text-primary">{item.boxBarCode}</TableCell>
                                        <TableCell>{item.noOfItems}</TableCell>
                                        <TableCell className="small">
                                            {item.weightKg ? `${item.weightKg}Kg` : '--'}<br />
                                            {item.boxDimension || '--'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Box className="d-flex justify-content-center gap-1">
                                                {item.isScannedInn ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> : <CancelIcon color="error" sx={{ fontSize: 16 }} />}
                                                {item.isScannedOut ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} /> : <CancelIcon color="error" sx={{ fontSize: 16 }} />}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="py-1">
                                            <div ref={(el) => (svgRefs.current[index] = el)} style={{ background: 'white', padding: '5px' }}>
                                                <Barcode value={item.boxBarCode} width={1.8} height={40} fontSize={10} margin={0} />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <IconButton size="small" color="primary" onClick={() => handlePrint(item, index)}>
                                                <PrintIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Notes */}
                    <Paper elevation={0} className="p-3 border rounded bg-light">
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <InfoIcon fontSize="small" className="me-1 opacity-50" />
                            <Typography variant="subtitle2" className="fw-bold">Reschedule Instructions</Typography>
                        </Box>
                        <Typography variant="caption" className="text-muted italic">
                            {dataInfo?.orderInstruction || "No additional instructions records for this rescheduled shipment."}
                        </Typography>
                    </Paper>
                </Box>
            </ModalBody>
        </Modal>
    );
};

export default connect()(OrderDetailsShippingViewDetailsModal);
